'use client';

import type { ReactNode } from 'react';

import { Check, ChevronRight, Clock, Lock, Plus, UserRound, Users, X } from 'lucide-react';
import type { ClientPerson, GatheringDoor } from '@/lib/types';
import { Avatar } from './Avatar';
import { offersFor } from './RegistrationTypes';
import { guestReady, type GuestDraft } from '@/lib/guests';
import { useDockRequest } from './PayDock';
import { useBottomDock } from '@/lib/bottom-dock';
import { fmtInstantDay, fmtMoney } from '@/lib/format';
import { netPrice } from '@/lib/discounts';

/**
 * THE SIGN-UP IS THE PANEL
 * ========================
 *
 * Not a Register button that opens a sheet that asks who: the household is
 * right here, and ticking people IS starting the registration. It is the
 * first question anyway ("who is this for"), it is the question the viewer
 * can answer without thinking, and answering it on the page means the sheet
 * opens already knowing.
 *
 * Each person says what is true for them: already registered and as what,
 * holding a seat mid-checkout, or available with the registrations they
 * could pick. A registration that is closed to somebody (volunteering needs
 * a current CPP approval) is still shown, with a lock, because a person who
 * cannot serve yet needs to know that and why, and a missing row answers
 * neither.
 *
 * PRICES ARE WHAT THIS PERSON PAYS. What a registration costs depends on who
 * it is for, and the server has already worked that out per person by the
 * time this list is drawn — so the pill says their number, with the sticker
 * price struck beside it where a discount moved it. Showing $10 here and $8
 * two screens later reads as a correction, not a discount.
 *
 * SOMEONE ELSE is a row, not a sheet. A friend riding along is the same
 * question as everybody else on this list ("who is coming"), so it is
 * answered in the same place: a name, typed here, joining the party. The
 * sheet still opens on Register, already knowing about them.
 */

/**
 * A person who is not in the household, named on the page.
 *
 * The email is not optional, and not for confirmations: it is how the server
 * finds them. A typed-in guest is matched to an existing MP contact by email
 * and refused when there is no match, because creating a contact from a
 * public form is how a church ends up with three of everybody. The phone is
 * the second thing to try, the way MP's own widget does it.
 */

/**
 * A heading over a run of people, and the only reason it exists is to answer
 * "why is he on this list, he doesn't live here".
 *
 * That question has one answer, so there is one heading. It used to carry
 * MP's own category off `Relationship_Types` — Primary Family, Extended
 * Family, In-Laws, Step & Foster, Emergency & Care — which sorted a list of
 * six people into four headed sections and made a filing cabinet out of a
 * question about who is coming. Each person still says what they are to the
 * reader beside their name, which is the part anybody reads.
 */
function GroupLabel({ children }: { children: ReactNode }) {
  return <p className="kicker text-slate pt-3 pb-1">{children}</p>;
}

/**
 * Who the viewer can put on this registration, in the order they would think
 * of them: the household, then everybody MP connects them to, closest first,
 * then the claims still waiting to be agreed to.
 *
 * Only relationships whose type carries `Allow_Online_Registration` reach this
 * list at all — a neighbour is fine to NAME on a profile and is not somebody
 * you may sign up, and offering a row that cannot be ticked is a worse answer
 * than leaving it out.
 */
function groupsOf(roster: ClientPerson[]) {
  const out: { key: string; label: string | null; people: ClientPerson[] }[] = [];
  for (const person of roster) {
    // The closest first inside it, which is what `tieSort` already did — the
    // order MP's categories used to be headings for survives as the order.
    const key = person.tie;
    const label =
      key === 'household'
        ? null
        : key === 'pending'
          ? 'Waiting to be confirmed'
          : // NOT "family". MP allows sixteen relationship types to register
            // online and three of them are nobody's family: Adult Cared for
            // by, Foreign Exchange Student of, Legal Ward of. The word has to
            // cover the carer and the exchange student as honestly as it
            // covers a grandmother. The sheet's own step says the same.
            'Other relationships';
    const last = out[out.length - 1];
    if (last && last.key === key) last.people.push(person);
    else out.push({ key, label, people: [person] });
  }
  return out;
}

export interface SignUpProps {
  doors: GatheringDoor[];
  people: ClientPerson[];
  /** Contact id to what they are already registered as, from their invoice line. */
  registeredAs: Map<number, string>;
  selected: Set<number>;
  onToggle: (contactId: number) => void;
  /** Guests typed in on the page, who ride along into the sheet. */
  guests: GuestDraft[];
  onAddGuest: () => void;
  /** Reopen the add-person sheet on a draft already started. */
  onEditGuest: (key: string) => void;
  onRemoveGuest: (key: string) => void;
  onContinue: () => void;
  signedIn: boolean;
  signIn: () => void;
  invoiceLink: (guid: string) => string;
  /** Open somebody's whole invoice, from their row. */
  onOpenInvoice?: (guid: string) => void;
  /** Contacts a card above this list already spelled out. */
  hide?: Set<number>;
}

/**
 * KEYBOARD FOCUS ON A PERSON IS A FILLED ROW, NOT A RING.
 *
 * The browser's ring is right for a lone button and wrong for a list of
 * faces: it draws a hard accent-coloured box around one person's photo, name
 * and pills, which reads as an error state rather than "this is where you
 * are". It also shows up when nobody asked for it — closing the registration
 * sheet hands focus back to the row that opened it, and Chrome treats that
 * restored focus as keyboard focus, so the ring lands on somebody you ticked
 * with the mouse a moment ago.
 *
 * A filled row says the same thing quietly, and it is still there for anyone
 * tabbing through the household.
 */
const ROW_FOCUS = 'outline-none focus-visible:bg-mist';

/**
 * Whole names. A household holds two Lukes as often as not, and the person
 * reading this is about to pay for them.
 */
function fullName(p: ClientPerson): string {
  return `${p.firstName} ${p.lastName}`.trim() || p.firstName;
}

/**
 * Who they are to the reader, from MP's own relationships: Wife, Son,
 * Grandmother. The viewer's own row says so instead, since "you" is the one
 * relationship a list of your household cannot get wrong.
 */
function Relation({ person }: { person: ClientPerson }) {
  const word = person.isViewer ? 'You' : person.relationship;
  if (!word) return null;
  // Never truncated with the name: a row that reads "Weston Wirg…" still has
  // to say Son.
  return <span className="text-slate shrink-0 text-[0.7rem] font-semibold">{word}</span>;
}

export function SignUp({
  doors,
  people,
  registeredAs,
  selected,
  onToggle,
  guests,
  onAddGuest,
  onEditGuest,
  onRemoveGuest,
  onContinue,
  signedIn,
  signIn,
  invoiceLink,
  onOpenInvoice,
  hide,
}: SignUpProps) {
  // A guest counts once we have enough to find them.
  const party = selected.size + guests.filter(guestReady).length;
  // Before the early return: a hook that only sometimes runs is not a hook.
  const openDock = useDockRequest();
  // A party of nobody has nothing to press, so nothing floats.
  const { slot, dock } = useBottomDock(party > 0);

  if (!signedIn) {
    return (
      <div className="space-y-3">
        <button type="button" onClick={signIn} className="btn btn-primary w-full !py-4 !text-base">
          <Users className="h-5 w-5" />
          Sign in to register
        </button>
        <button type="button" onClick={onContinue} className="btn btn-quiet w-full">
          Register without signing in
        </button>
      </div>
    );
  }

  /**
   * The household first, then everybody MP says is family who lives somewhere
   * else, then the claims still waiting to be agreed to. One list rather than
   * three cards, because to the person reading it this is just their people —
   * the heading exists only so nobody wonders why their stepson is in here
   * when he does not live here.
   */
  const RANK = { household: 0, related: 1, pending: 2 } as const;
  const roster = people
    .filter((person) => !hide?.has(person.contactId))
    .slice()
    .sort(
      (a, b) =>
        RANK[a.tie] - RANK[b.tie] ||
        a.tieSort - b.tieSort ||
        (a.tieGroup ?? '').localeCompare(b.tieGroup ?? '')
    );
  const groups = groupsOf(roster);

  return (
    <div className="space-y-3">
      {/* No heading here: the card carries it, at the top where it belongs. */}
      <ul className="divide-mist divide-y">
        {groups.flatMap((group) =>
          group.people.map((person, i) => {
            const opensGroup = i === 0 && group.label !== null;
            const already = registeredAs.get(person.contactId) ?? null;
            const isIn = person.already === 'registered';
            const isHolding = person.already === 'pending';
            const on = selected.has(person.contactId);
            const offered = offersFor(doors, person);
            // With one way in there is nothing to tell apart: every row would
            // carry the same pill. A locked one still says why.
            const offer = doors.length === 1 ? { ...offered, open: [] } : offered;

            /**
             * A claim nobody has answered yet. It is listed so the viewer can
             * see they already asked and does not ask again, and it can do
             * nothing else: a relationship somebody asserted about a record
             * that is not theirs buys no seat until a human agrees to it.
             */
            if (person.tie === 'pending') {
              return (
                <li key={person.contactId}>
                  {opensGroup && <GroupLabel>{group.label}</GroupLabel>}
                  <div className="flex items-center gap-3 py-3 opacity-70">
                    <span className="border-line bg-mist h-5 w-5 shrink-0 rounded-md border" />
                    <Avatar
                      name={`${person.firstName} ${person.lastName}`}
                      image={person.image}
                      size={36}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline gap-1.5">
                        <span className="text-navy truncate text-sm font-bold">
                          {fullName(person)}
                        </span>
                        <Relation person={person} />
                      </div>
                      <div className="text-slate flex items-center gap-1 text-xs font-semibold">
                        <Clock className="h-3.5 w-3.5" />
                        Waiting on their family to confirm
                      </div>
                    </div>
                  </div>
                </li>
              );
            }

            if (isIn || isHolding) {
              // Somebody already in is a way into their invoice: what was paid,
              // by whom, and everyone else it covers. A household's registration
              // is one receipt, not one per person.
              //
              // A seat still being HELD is already on the screen, as the cart
              // in the corner. Tapping the row opens that rather than a sheet
              // repeating its numbers — same registration, one place to look
              // at it. A settled one has no cart to open, so it opens the
              // receipt.
              const guid = person.invoiceGuid;
              const toCart = Boolean(guid && isHolding && openDock);
              const open = Boolean(guid) && (toCart || Boolean(onOpenInvoice));
              const row = (
                <>
                  <Avatar
                    name={`${person.firstName} ${person.lastName}`}
                    image={person.image}
                    size={36}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline gap-1.5">
                      <span className="text-navy truncate text-sm font-bold">
                        {fullName(person)}
                      </span>
                      <Relation person={person} />
                    </div>
                    <div className={`text-xs ${isIn ? 'text-teal-deep' : 'text-amber'}`}>
                      {isIn ? (
                        <span className="inline-flex items-center gap-1 font-semibold">
                          <Check className="h-3.5 w-3.5" />
                          {already ? `Registered as ${already}` : 'Registered'}
                        </span>
                      ) : (
                        // No "finish paying" link: the invoice that needs paying
                        // is the card docked to the corner.
                        <span className="font-semibold">Seat held</span>
                      )}
                    </div>
                  </div>
                  {open && <ChevronRight className="text-slate h-4 w-4 shrink-0" />}
                </>
              );
              return (
                <li key={person.contactId}>
                  {opensGroup && <GroupLabel>{group.label}</GroupLabel>}
                  {open ? (
                    <button
                      type="button"
                      onClick={() =>
                        toCart ? openDock!(guid as string) : onOpenInvoice?.(guid as string)
                      }
                      className={`flex w-full items-center gap-3 py-3 text-left ${ROW_FOCUS}`}
                    >
                      {row}
                    </button>
                  ) : (
                    <div className="flex items-center gap-3 py-3">{row}</div>
                  )}
                </li>
              );
            }

            return (
              <li key={person.contactId}>
                {opensGroup && <GroupLabel>{group.label}</GroupLabel>}
                <button
                  type="button"
                  onClick={() => onToggle(person.contactId)}
                  aria-pressed={on}
                  className={`flex w-full items-start gap-3 py-3 text-left ${ROW_FOCUS}`}
                >
                  <span
                    className={`mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border transition ${
                      on ? 'bg-teal border-teal text-white' : 'border-line bg-white'
                    }`}
                  >
                    {on && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
                  </span>
                  <Avatar
                    name={`${person.firstName} ${person.lastName}`}
                    image={person.image}
                    size={36}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="flex items-baseline gap-1.5">
                      <span className="text-navy truncate text-sm font-bold">
                        {fullName(person)}
                      </span>
                      <Relation person={person} />
                    </span>
                    {/* An event with one registration has nothing to pill:
                      the price is stated once, above the list. */}
                    <span
                      className={
                        offer.open.length + offer.locked.length > 0
                          ? 'mt-1 flex flex-wrap gap-1'
                          : 'hidden'
                      }
                    >
                      {offer.open.map((d) => {
                        const net = netPrice(d.price, person.promos);
                        return (
                          <span
                            key={d.id}
                            className="bg-mist text-slate-deep rounded-full px-2 py-0.5 text-[0.65rem] font-bold"
                          >
                            {d.label}
                            {d.price > 0 && (
                              <span className="text-navy ml-1">
                                {net < d.price && (
                                  <s className="text-slate mr-1 font-normal">{fmtMoney(d.price)}</s>
                                )}
                                {net <= 0 ? 'Free' : fmtMoney(net)}
                              </span>
                            )}
                          </span>
                        );
                      })}
                      {offer.locked.map(({ door: d, unmet, when }) => (
                        <span
                          key={d.id}
                          title={unmet}
                          className="text-slate border-line inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[0.65rem] font-bold"
                        >
                          {/* A registration that has not opened yet is not
                            locked against this person, it is early. The date
                            says so on the pill; the lock is for the rules. */}
                          {when ? (
                            <>
                              {d.label}
                              <span className="font-semibold opacity-70">
                                {when === 'early'
                                  ? `opens ${fmtInstantDay(d.opensAt)}`
                                  : `closed ${fmtInstantDay(d.closesAt)}`}
                              </span>
                            </>
                          ) : (
                            <>
                              <Lock className="h-2.5 w-2.5" />
                              {d.label}
                            </>
                          )}
                        </span>
                      ))}
                    </span>
                  </span>
                </button>
              </li>
            );
          })
        )}

        {/*
         * Only people who exist. A draft still being typed lives in the sheet
         * and nowhere else — it used to appear here the moment the sheet
         * opened, as "Someone else · tap to finish adding them", which put a
         * row in the household card for somebody who was not yet a person
         * anywhere. Abandoning the sheet now discards the draft, so there is
         * nothing half-made left to resume.
         */}
        {guests
          .filter((g) => g.saved || guestReady(g))
          .map((guest, i) => {
            const ready = guestReady(guest);
            // A claim on somebody MP already held. It is on the list so the
            // viewer can see they asked, and it can do nothing else until the
            // other family answers.
            const waiting = guest.saved === 'pending';
            const name = `${guest.firstName} ${guest.lastName}`.trim();
            return (
              <li key={guest.key}>
                {/* Their own heading, and not for tidiness: without it the first
                  person you just added sits under "Waiting to be confirmed",
                  which is the one thing they are not. */}
                {i === 0 && <GroupLabel>Just added</GroupLabel>}
                {/*
                 * A person being added reads as a person, not as a form. The
                 * questions live in the sheet; this row is only what was
                 * answered, and tapping it reopens the sheet to change it.
                 */}
                <div className="flex items-center gap-3 py-3">
                  <button
                    type="button"
                    onClick={() => onEditGuest(guest.key)}
                    className={`flex min-w-0 flex-1 items-center gap-3 text-left ${ROW_FOCUS}`}
                  >
                    <span
                      className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md border transition ${
                        waiting
                          ? 'border-line bg-mist'
                          : ready
                            ? 'bg-teal border-teal text-white'
                            : 'border-line bg-white'
                      }`}
                    >
                      {!waiting && ready && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
                    </span>
                    {name ? (
                      <Avatar name={name} size={36} />
                    ) : (
                      <span className="bg-mist text-slate flex h-9 w-9 shrink-0 items-center justify-center rounded-full">
                        <UserRound className="h-4 w-4" />
                      </span>
                    )}
                    <span className="min-w-0 flex-1">
                      <span className="flex items-baseline gap-1.5">
                        <span className="text-navy truncate text-sm font-bold">
                          {name || 'Someone else'}
                        </span>
                        {guest.relationChoice && (
                          <span className="text-slate shrink-0 text-[0.7rem] font-semibold">
                            {guest.relationChoice}
                          </span>
                        )}
                      </span>
                      <span className="text-slate block truncate text-[0.7rem]">
                        {waiting ? (
                          <span className="inline-flex items-center gap-1 font-semibold">
                            <Clock className="h-3 w-3" />
                            Waiting on their family to confirm
                          </span>
                        ) : !ready ? (
                          'Tap to finish adding them'
                        ) : guest.addToHousehold ? (
                          'Joining your household'
                        ) : (
                          'Saved as a relationship'
                        )}
                      </span>
                    </span>
                  </button>
                  <button
                    type="button"
                    aria-label="Remove"
                    onClick={() => onRemoveGuest(guest.key)}
                    className="text-slate hover:text-rose shrink-0"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </li>
            );
          })}

        <li>
          <button
            type="button"
            onClick={onAddGuest}
            className={`text-teal-deep flex w-full items-center gap-2 py-3 text-sm font-bold ${ROW_FOCUS}`}
          >
            <span className="border-line flex h-5 w-5 items-center justify-center rounded-md border">
              <Plus className="h-3 w-3" />
            </span>
            Someone else
          </button>
        </li>
      </ul>

      {/*
       * Next, not Register: ticking people is the first step of the flow, and
       * the registration happens in the sheet this opens.
       *
       * IT DOCKS TO THE BOTTOM OF THE WINDOW UNTIL IT CAN SIT IN ITS OWN
       * PLACE. A big household runs the card past the bottom of the screen,
       * and the button under the list was then something you had to go and
       * find. While its own place is below the fold it rides the bottom edge;
       * the moment that place comes into view it settles there. Nothing
       * floats on a short list, and nothing floats before anyone is ticked.
       *
       * Written by hand rather than with `position: sticky`, which cannot
       * work on this site at all — see `useBottomDock`.
       */}
      <div ref={slot} className="-mb-5" style={dock ? { height: dock.height } : undefined}>
        <div
          className={
            dock
              ? 'bg-paper fixed bottom-0 z-40 px-5 pt-3 pb-5 shadow-[0_-10px_24px_-18px_rgba(16,24,32,0.45)]'
              : // Sitting in the card, it IS the card's bottom edge: the bar
                // bleeds to both sides and paints over the padding box, so
                // without the card's own radius it squares off the corners.
                'bg-paper relative -mx-5 rounded-b-[var(--radius-card)] px-5 pt-3 pb-5'
          }
          style={dock ? { left: dock.left, width: dock.width } : undefined}
        >
          {/* The names do not stop dead at the bar: they fade into it. */}
          {dock && (
            <span
              aria-hidden
              className="pointer-events-none absolute inset-x-0 bottom-full h-6 bg-gradient-to-t from-[var(--color-paper)] to-transparent"
            />
          )}
          <button
            type="button"
            disabled={party === 0}
            onClick={onContinue}
            className="btn btn-primary w-full !py-4 !text-base"
          >
            Next
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
}

// Re-exported so the sheet and the page keep importing them from one place.
export { guestReady, type GuestDraft };
